import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import * as React from 'react'
import dynamic from 'next/dynamic'
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth'
import { auth, googleProvider } from '@configs/firebase'
import { useRouter } from 'next/router'
// import UserDetailsModal from '@components/organisms/UserDetailsModal'
const UserDetailsModal = dynamic(
  () => import('@components/organisms/UserDetailsModal')
)
import {
  AUTH_TOKEN_KEY,
  LOCALSTORAGE_CART_KEY,
  LOCALSTORAGE_CID,
  LOCALSTORAGE_INACTIVE_CART_ITEMS_KEY,
  LOCALSTORAGE_MUID,
  LOCALSTORAGE_PL,
  LOCALSTORAGE_SAVED_CART_ITEMS_KEY,
  LOCALSTORAGE_WISHLIST_KEY,
} from '@constants/localstorageKeys'
import {
  FacebookAuthProvider,
  User as FirebaseUser,
  GithubAuthProvider,
} from '@firebase/auth'
import AuthAPI from '@services/api/auth/auth'
import { User } from '@type/user'
const LoginModal = dynamic(() => import('@components/organisms/LoginModal'))
import getUser from '@services/api/user/getUser'
import { isProtected } from '@constants/protectedPages'
import { CourseProgress, CourseVersion } from '@ugu/types'
import { publicRoutes } from '@utils/publicRoutes'
import trackActivity from '@services/api/user/marketing/trackActivity'
import useGetMyCourses from '@services/react-query/hooks/user/queries/useGetMyCourses'
import { queryClient } from 'pages/_app'
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'
import Cookies from 'js-cookie'
import { del } from 'idb-keyval'
import { customStore } from '@utils/storage'
import useGetReferralUser from '@services/react-query/hooks/referrals/useGetReferralUser'
import { ReferralUser } from '@type/referrals'

type AuthContext = {
  isAuthLoading: boolean
  isAPILoading: boolean
  isAuthenticated: boolean
  user: User | null
  firebaseUser: FirebaseUser | null
  error: any
  login: (
    email: string,
    password: string,
    redirectionUrl?: string
  ) => Promise<void>
  register: (
    email: string,
    password: string,
    setIsRegistering?: Dispatch<SetStateAction<boolean>>,
    onSuccess?: () => void,
    onError?: (err: any) => void
  ) => Promise<void>
  loginWithGoogle: () => Promise<void>
  loginWithFacebook: () => Promise<void>
  loginWithGithub: () => Promise<void>
  logout: () => void
  forgetPassword: (email: string) => Promise<void>
  refreshUser: () => Promise<void>
  updateUser: (userData: User) => void
  setShowAuthModal: Dispatch<SetStateAction<boolean>>
  userCourses: {
    courses: CourseVersion[]
    progress: CourseProgress[]
  }
  isFetchingUserCourses: boolean
  setIsEditorOnFullScreen: Dispatch<React.SetStateAction<boolean>>
  isEditorOnFullScreen: boolean
  setUserCourses: React.Dispatch<
    React.SetStateAction<{
      courses: CourseVersion[]
      progress: CourseProgress[]
    }>
  >
  showUserDetailModal: boolean
  refetchMyCourses: (options?: RefetchOptions) => Promise<
    QueryObserverResult<
      {
        courses: CourseVersion[]
        progress: CourseProgress[]
      },
      Error
    >
  >
  referralUser: ReferralUser | undefined
}
const Index = createContext<AuthContext | undefined>(undefined)
const useAuth = () => {
  const context = useContext(Index)
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider')
  }
  return context
}
const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const router = useRouter()
  const { utfd, marketingUserId, campaignId } = router.query
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false)
  const [isAuthLoading, setIsAuthLoading] = useState(true)
  const [isAPILoading, setIsAPILoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [userCourses, setUserCourses] = useState<{
    courses: CourseVersion[]
    progress: CourseProgress[]
  }>({ courses: [], progress: [] })
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showUserDetailModal, setShowUserDetailModal] = useState<boolean>(false)
  const { data: referralUser } = useGetReferralUser(user?._id)

  const [isEditorOnFullScreen, setIsEditorOnFullScreen] =
    useState<boolean>(false)
  const {
    data: coursesData,
    refetch,
    isLoading: isFetchingUserCourses,
  } = useGetMyCourses(
    (userCourses.courses?.length === 0 && !!user?.purchased_courses?.length) ||
      !user,
    isAuthLoading
  )

  const getReferralCodeFromLocalStorage: () => string | null = () => {
    const discountData = localStorage.getItem('referral-code')
    if (discountData) {
      const { code, expiry } = JSON.parse(discountData)
      if (new Date().getTime() < expiry) {
        return code
      } else {
        localStorage.removeItem('referral-code')
      }
    }
    return null
  }

  const getMarketingData = () => {
    return {
      marketingUserId: localStorage.getItem(LOCALSTORAGE_MUID),
      campaignId: localStorage.getItem(LOCALSTORAGE_CID),
      personalizedLink: localStorage.getItem(LOCALSTORAGE_PL),
    }
  }

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      setIsAuthLoading(true)
      setIsAuthenticated(false)
      if (firebaseUser) {
        try {
          const tokenId = await firebaseUser.getIdToken(false)
          const wasLoggedInBefore = !!Cookies.get(AUTH_TOKEN_KEY)
          // localStorage.setItem(AUTH_TOKEN_KEY, tokenId)
          Cookies.set(AUTH_TOKEN_KEY, tokenId)
          const referralCode = getReferralCodeFromLocalStorage()
          const { marketingUserId, personalizedLink } = getMarketingData()
          const freshUser = await queryClient.fetchQuery({
            queryKey: ['unified-auth', tokenId],
            queryFn: () =>
              AuthAPI(
                tokenId,
                referralCode,
                marketingUserId as string,
                personalizedLink as string
              ),
            staleTime: 5 * 60 * 1000,
            gcTime: 10 * 60 * 1000,
          })
          setIsAuthLoading(false)
          setShowAuthModal(false)
          setIsAPILoading(false)
          setIsAuthenticated(true)
          setUser(freshUser)
          setFirebaseUser(firebaseUser)
          setError(null)
          if (!freshUser.date_of_birth) {
            const [firstName = '', lastName = ''] =
              firebaseUser.displayName?.split(' ') || []
            if (tokenId !== utfd) {
              if (publicRoutes.includes(router.asPath.split('?')[0])) return
              router.push(
                router.asPath.split('?')[0] +
                  '?utfd=' +
                  tokenId +
                  '&fn=' +
                  firstName +
                  '&ln=' +
                  lastName
              )
            }
          } else {
            if (!wasLoggedInBefore) refetch()
          }
          if (marketingUserId) localStorage.removeItem(LOCALSTORAGE_MUID)
          if (personalizedLink) localStorage.removeItem(LOCALSTORAGE_PL)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
          setIsAuthLoading(false)
          setUser(null)
          setFirebaseUser(null)
          setIsAuthenticated(false)
          setError(err.response.data.message)
          logout(false)
          setIsAPILoading(false)
        }
      } else {
        setIsAuthLoading(false)
        setIsAuthenticated(false)
        setIsAPILoading(false)
        setUser(null)
        setFirebaseUser(null)
        Cookies.remove(AUTH_TOKEN_KEY)
      }
    })
    return () => unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    if (!utfd) return
    const token = Cookies.get(AUTH_TOKEN_KEY)
    if (!token || utfd !== token) return

    // Wait until the user is loaded before deciding — otherwise a stale utfd
    // could open the modal before we know the profile is already complete.
    if (!user) return

    // Only prompt for details when they're actually missing. If the profile is
    // already complete (e.g. a leftover utfd from a bookmarked/shared link),
    // strip the stale params from the URL instead of showing the modal.
    if (!user.date_of_birth) {
      setShowUserDetailModal(true)
    } else {
      const restQuery = { ...router.query }
      delete restQuery.utfd
      delete restQuery.fn
      delete restQuery.ln
      router.replace(
        { pathname: router.pathname, query: restQuery },
        undefined,
        { shallow: true }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utfd, user])

  useEffect(() => {
    if (coursesData) {
      const { courses, progress } = coursesData
      setUserCourses({
        courses,
        progress,
      })
    }
  }, [coursesData])

  useEffect(() => {
    const interval = setInterval(
      async () => {
        if (firebaseUser) {
          const tokenId = await firebaseUser.getIdToken(true)
          Cookies.set(AUTH_TOKEN_KEY, tokenId)
        }
      },
      10 * 60 * 1000
    )
    return () => clearInterval(interval)
  }, [firebaseUser])

  const handleMarketingUser = async () => {
    try {
      const data: {
        marketingUserId?: string
        campaignId?: string
        user?: string
        personalizedLink?: string
      } = {}
      if (marketingUserId) {
        data.marketingUserId = String(marketingUserId)?.trim() as string
        localStorage.setItem(LOCALSTORAGE_MUID, marketingUserId as string)
      }
      if (campaignId) {
        data.campaignId = String(campaignId)?.trim() as string
        localStorage.setItem(LOCALSTORAGE_CID, campaignId as string)
      }
      await trackActivity(data)
    } catch (error) {}
  }

  useEffect(() => {
    if ((marketingUserId || campaignId) && !isAuthLoading) handleMarketingUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketingUserId, campaignId, isAuthLoading])

  useEffect(() => {
    if (isProtected(router.pathname) && !isAuthenticated && !isAuthLoading) {
      setShowAuthModal(true)
    }
  }, [isAuthLoading, isAuthenticated, router.pathname])

  const initializeGSI = useCallback(() => {
    const authHandler = (response: any) => {
      const idToken = response.credential
      const credential = GoogleAuthProvider.credential(idToken)
      try {
        return signInWithCredential(auth, credential)
      } catch (error) {}
    }
    // @ts-ignore
    window?.google?.accounts.id.initialize({
      client_id: process.env.NEXT_PUBLIC_FIREBASE_WEB_CLIENT_ID,
      callback: authHandler,
    })
    // @ts-ignore
    window?.google?.accounts.id.prompt((notification: any) => notification)
  }, [])

  useEffect(() => {
    if (isAuthLoading || user) return

    const scriptId = 'google-gsi-script'
    const existingScript = document.getElementById(scriptId)

    if (!existingScript) {
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.id = scriptId

      script.onload = () => {
        initializeGSI()
      }

      document.body.appendChild(script)
    } else {
      initializeGSI()
    }
  }, [isAuthLoading, user])

  const login = async (
    email: string,
    password: string,
    redirectionUrl: string | null = null
  ) => {
    try {
      await signInWithEmailAndPassword(auth, email, password)
      redirectionUrl && router.push(redirectionUrl)
    } catch (err: unknown) {
      // Error occurred during sign in
      // We assert the error to have the structure { code: string; message: string }
      // based on Firebase documentation
      if (typeof err === 'object' && err !== null) {
        const firebaseError = err as { code: string; message: string }
        setError(firebaseError.code)
      } else {
        // Handle unexpected error types
        const errorText = 'auth/unexpected-error'
        setError(errorText)
      }

      setIsAuthLoading(false)
      setUser(null)
      setFirebaseUser(null)
      setIsAuthenticated(false)
    }
  }

  const register = async (
    email: string,
    password: string,
    setIsRegistering?: Dispatch<SetStateAction<boolean>>,
    onSuccess?: () => void,
    onError?: (err: any) => void
  ) => {
    if (setIsRegistering) {
      setIsRegistering(true)
    }
    try {
      const response = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      )
      const tokenId = await response.user.getIdToken(false)
      if (onSuccess) {
        onSuccess()
      }

      router.push(
        router.asPath.split('?')[0] +
          '?utfd=' +
          tokenId +
          '&fn=' +
          '' +
          '&ln=' +
          ''
      )
    } catch (err: any) {
      setIsAuthLoading(false)
      setUser(null)
      setFirebaseUser(null)
      setIsAuthenticated(false)
      if (onError) {
        onError(err)
      }
    } finally {
      if (setIsRegistering) {
        setIsRegistering(false)
      }
    }
  }

  const loginWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider)
    } catch (err: any) {
      setIsAuthLoading(false)
      setUser(null)
      setFirebaseUser(null)
      setIsAuthenticated(false)
      setError(err)
    }
  }

  const loginWithFacebook = async () => {
    try {
      const provider = new FacebookAuthProvider()
      await signInWithPopup(auth, provider)
    } catch (err: any) {
      setIsAuthLoading(false)
      setUser(null)
      setFirebaseUser(null)
      setIsAuthenticated(false)
      setError(err)
    }
  }

  const loginWithGithub = async () => {
    try {
      const provider = new GithubAuthProvider()
      await signInWithPopup(auth, provider)
    } catch (err: any) {
      setIsAuthLoading(false)
      setUser(null)
      setFirebaseUser(null)
      setIsAuthenticated(false)
      setError(err)
    }
  }

  const removeQueriesCache = async () => {
    del('REACT_QUERY_OFFLINE_CACHE', customStore)
  }

  const logout = async (redirectToHome = true) => {
    await removeQueriesCache()
    localStorage.setItem(LOCALSTORAGE_CART_KEY, '[]')
    localStorage.setItem(LOCALSTORAGE_WISHLIST_KEY, '[]')
    localStorage.setItem(LOCALSTORAGE_SAVED_CART_ITEMS_KEY, '[]')
    localStorage.setItem(LOCALSTORAGE_INACTIVE_CART_ITEMS_KEY, '[]')
    localStorage.removeItem('referral-code')
    Cookies.remove(AUTH_TOKEN_KEY)

    setIsAuthLoading(false)
    router.push('/')
    auth.signOut()
    if (redirectToHome) {
      window.location.href = '/'
    }
  }
  const forgetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email)
    } catch (err: any) {
      setIsAuthLoading(false)
      setError(err)
    }
  }
  const refreshUser = () => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const response = await getUser()
        setUser(response)
        resolve()
      } catch (err) {
        reject(err)
      }
    })
  }

  const updateUser = (userData: User) => {
    setUser(userData)
    queryClient
      .getQueryCache()
      .findAll({ queryKey: ['unified-auth'] })
      .forEach(({ queryKey }) => {
        queryClient.setQueryData(queryKey, () => {
          return userData
        })
      })
  }

  const contextValues = {
    isAuthLoading,
    isAPILoading,
    isAuthenticated,
    user,
    firebaseUser,
    error,
    login,
    register,
    loginWithGoogle,
    logout,
    forgetPassword,
    refreshUser,
    updateUser,
    setShowAuthModal,
    userCourses,
    isFetchingUserCourses,
    setIsEditorOnFullScreen,
    isEditorOnFullScreen,
    setUserCourses,
    showUserDetailModal,
    refetchMyCourses: refetch as any,
    referralUser,
    loginWithFacebook,
    loginWithGithub,
  }
  return (
    <Index.Provider value={contextValues}>
      {children}
      {showAuthModal && (
        <LoginModal
          isOpen={showAuthModal}
          onClose={() => {
            if (error) {
              setError(null)
            }
            setShowAuthModal(false)
          }}
        />
      )}
      {showUserDetailModal && (
        <UserDetailsModal
          setShowUserDetailModal={setShowUserDetailModal}
          onClose={(ask) => {
            if (ask) {
              if (confirm('Are you sure you want to log out?')) {
                auth.signOut().then(() => {
                  logout()
                })
              }
            } else {
              setShowUserDetailModal(false)
            }
          }}
          utfd={utfd?.toString()}
        />
      )}
    </Index.Provider>
  )
}
export { useAuth, AuthProvider }
